import axios from 'axios';
import { env } from '@/shared/config/env';
import { toApiError } from '@/shared/api/errors';

/** Credenciais enviadas em todo request autenticado (headers `email` e `token`). */
export interface Credentials {
  email: string;
  token: string;
}

type CredentialsReader = () => Credentials | null;
type UnauthorizedHandler = () => void;

let readCredentials: CredentialsReader = () => null;
let onUnauthorized: UnauthorizedHandler = () => {};

/**
 * Liga o cliente HTTP à sessão.
 *
 * Chamado uma vez pelo `AuthProvider`. Sem isso, os serviços não conseguiriam
 * enviar as credenciais sem receber a sessão por parâmetro em cada chamada —
 * e cada componente acabaria repetindo a montagem dos headers, como acontecia
 * no painel antigo.
 */
export function configureApiSession(options: { readCredentials: CredentialsReader; onUnauthorized: UnauthorizedHandler }): void {
  readCredentials = options.readCredentials;
  onUnauthorized = options.onUnauthorized;
}

export const api = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(config => {
  if (config.skipAuth) return config;

  const credentials = readCredentials();
  if (credentials) {
    config.headers.set('email', credentials.email);
    config.headers.set('token', credentials.token);
  }
  return config;
});

/**
 * Centraliza o tratamento de 401: encerra a sessão uma única vez, em um único
 * lugar, e converte tudo para `ApiError`.
 */
api.interceptors.response.use(
  response => response,
  (error: unknown) => {
    const apiError = toApiError(error);
    if (apiError.isUnauthorized) {
      onUnauthorized();
    }
    return Promise.reject(apiError);
  },
);
